import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import axios from "axios";
import { toast } from "sonner";
import { Briefcase, RefreshCw, MessageSquare } from "lucide-react";
import { StatsPanel } from "../components/admin/StatsPanel";
import { SubmissionRow } from "../components/admin/SubmissionRow";
import { ContactsPanel } from "../components/admin/ContactsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [password, setPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [jobs, setJobs] = useState({});
  const [fetchingJobs, setFetchingJobs] = useState({});
  const [sendingDelivery, setSendingDelivery] = useState({});
  const [sendingTemplate, setSendingTemplate] = useState({});
  const [contacts, setContacts] = useState([]);
  const [showContacts, setShowContacts] = useState(false);

  const authenticate = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/stats?password=${password}`);
      setStats(response.data);
      setIsAuthenticated(true);
      fetchSubmissions();
    } catch (e) {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/admin/submissions?password=${password}`);
      setSubmissions(response.data.submissions);
    } catch (e) {
      toast.error("Failed to fetch submissions");
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats?password=${password}`);
      setStats(response.data);
    } catch (e) {
      // stats refresh failed silently
    }
  };

  const updateStatus = async (submissionId, newStatus) => {
    try {
      await axios.post(`${API}/admin/submission/${submissionId}/status?password=${password}&new_status=${newStatus}`);
      toast.success(`Status updated to ${newStatus}`);
      fetchSubmissions();
      fetchStats();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const fetchJobsForSubmission = async (submissionId) => {
    setFetchingJobs(prev => ({ ...prev, [submissionId]: true }));
    try {
      const response = await axios.get(`${API}/admin/fetch-jobs/${submissionId}?password=${password}`);
      setJobs(prev => ({ ...prev, [submissionId]: response.data.jobs }));
      toast.success(`Found ${response.data.total_found} jobs`);
      fetchSubmissions();
    } catch (e) {
      toast.error("Failed to fetch jobs");
    } finally {
      setFetchingJobs(prev => ({ ...prev, [submissionId]: false }));
    }
  };

  const loadExistingJobs = async (submissionId) => {
    try {
      const response = await axios.get(`${API}/admin/jobs/${submissionId}?password=${password}`);
      setJobs(prev => ({ ...prev, [submissionId]: response.data.jobs }));
    } catch (e) {
      // silently handle
    }
  };

  const sendDeliveryEmail = async (submissionId) => {
    setSendingDelivery(prev => ({ ...prev, [submissionId]: true }));
    try {
      const response = await axios.post(`${API}/admin/send-delivery/${submissionId}?password=${password}`);
      toast.success(response.data.message);
      fetchSubmissions();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send delivery email");
    } finally {
      setSendingDelivery(prev => ({ ...prev, [submissionId]: false }));
    }
  };

  const sendTemplateEmail = async (submissionId, template) => {
    setSendingTemplate(prev => ({ ...prev, [submissionId]: template }));
    try {
      const response = await axios.post(`${API}/admin/send-template-email/${submissionId}?password=${password}&template=${template}`);
      toast.success(response.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingTemplate(prev => ({ ...prev, [submissionId]: null }));
    }
  };

  const updateSubmissionField = async (submissionId, field, value) => {
    try {
      await axios.post(`${API}/admin/submission/${submissionId}/update?password=${password}`, { [field]: value });
      toast.success(`Updated ${field}`);
      fetchSubmissions();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/admin/contacts?password=${password}`);
      setContacts(response.data.contacts);
    } catch (e) {
      // silently handle
    }
  };

  const toggleExpand = (submissionId) => {
    if (expandedSubmission === submissionId) {
      setExpandedSubmission(null);
    } else {
      setExpandedSubmission(submissionId);
      if (!jobs[submissionId]) {
        loadExistingJobs(submissionId);
      }
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="bg-white border border-zinc-200 p-8 max-w-md w-full">
          <div className="flex items-center gap-2 mb-6">
            <Briefcase className="w-6 h-6 text-[#002FA7]" />
            <span className="font-black text-xl tracking-tight text-zinc-900">Admin Dashboard</span>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-zinc-700">Admin Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="mt-1 rounded-sm"
                onKeyPress={(e) => e.key === 'Enter' && authenticate()}
                data-testid="admin-password-input"
              />
            </div>
            <Button
              onClick={authenticate}
              disabled={loading || !password}
              className="w-full bg-[#002FA7] text-white hover:bg-[#002280] rounded-sm"
              data-testid="admin-login-button"
            >
              {loading ? "Authenticating..." : "Login"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-[#002FA7]" />
            <span className="font-black text-xl tracking-tight text-zinc-900">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={showContacts ? "default" : "outline"}
              className="rounded-sm"
              onClick={() => { setShowContacts(!showContacts); if (!showContacts) fetchContacts(); }}
              data-testid="contacts-toggle"
            >
              <MessageSquare className="w-4 h-4 mr-1" /> Contacts
            </Button>
            <Link to="/">
              <Button variant="outline" className="rounded-sm">View Site</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <StatsPanel stats={stats} />

        {/* Submissions List */}
        <div className="bg-white border border-zinc-200">
          <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
            <h2 className="font-semibold text-zinc-900">Submissions</h2>
            <Button variant="ghost" size="sm" onClick={fetchSubmissions} data-testid="refresh-submissions">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>

          {submissions.length === 0 ? (
            <div className="p-8 text-center text-zinc-500">No submissions yet</div>
          ) : (
            <div className="divide-y divide-zinc-200">
              {submissions.map((sub) => (
                <SubmissionRow
                  key={sub.submission_id}
                  sub={sub}
                  isExpanded={expandedSubmission === sub.submission_id}
                  jobs={jobs[sub.submission_id]}
                  fetchingJobs={fetchingJobs[sub.submission_id]}
                  sendingDelivery={sendingDelivery[sub.submission_id]}
                  sendingTemplate={sendingTemplate[sub.submission_id]}
                  onToggleExpand={toggleExpand}
                  onFetchJobs={fetchJobsForSubmission}
                  onSendDelivery={sendDeliveryEmail}
                  onSendTemplate={sendTemplateEmail}
                  onUpdateStatus={updateStatus}
                  onUpdateField={updateSubmissionField}
                />
              ))}
            </div>
          )}
        </div>

        {showContacts && <ContactsPanel contacts={contacts} onRefresh={fetchContacts} />}
      </main>
    </div>
  );
}
